"use client";

import React, { useMemo } from "react";
import { LayoutPricingCards } from "../terra/layouts/LayoutPricingCards/LayoutPricingCards";
import { ThemeDivider } from "@/components/terra/component/ThemeDivider/ThemeDivider";
import { documentToHtmlString } from "@contentful/rich-text-html-renderer";
import { handleButtons } from "@/lib/utils";
import { formatLayoutCurrencyList, getLayoutCurrencyCodes } from "@/lib/pricing/layoutPricingCurrency";
import { buildContentfulPriceList, resolveCardPriceList } from "@/lib/pricing/buildContentfulPriceList";
import { useEcomStore } from "@/context/ecom/EcomContext";
import { useLayoutPricingCardPrices } from "./useLayoutPricingCardPrices";
export default function MXPLayoutPricingCards(props: any) {
  const {
    value,
    index,
    divider,
    headerElement,
    lang,
    config
  } = props;
  const ecomStore = useEcomStore();
  const [state] = ecomStore ?? [];
  const viewMoreLabel = config?.commonDefaultValues?.labelForViewMore;
  const viewLessLabel = config?.commonDefaultValues?.labelForViewLess;
  const productPriceLists = useLayoutPricingCardPrices(value, state?.currency, state?.countryCode ?? undefined, lang ?? undefined);
  const buttons = handleButtons(value.buttonsCollection?.items, true, lang);
  const layoutCurrencyCodes = getLayoutCurrencyCodes(value?.currencyList, state?.currency);
  const cardData = (cards: any) => {
    return cards?.filter(Boolean).map((item: any) => {
      const buttonData = handleButtons(item.buttonsCollection?.items, false, lang);
      const cardId = item?.sys?.id;
      const contentfulPriceList = buildContentfulPriceList(item, value?.currencyList);
      const priceList = resolveCardPriceList(contentfulPriceList, cardId ? productPriceLists[cardId] ?? {} : {});
      const hasPriceList = Object.keys(priceList).length > 0;
      const activeCurrency = state?.currency && priceList[state.currency] ? state.currency : Object.keys(priceList)[0] ?? layoutCurrencyCodes[0] ?? "";
      const activePriceEntry = activeCurrency ? priceList[activeCurrency] : undefined;
      const manualOverride = Boolean(contentfulPriceList[activeCurrency]?.currency);
      return {
        cardType: "normal",
        header: item.cardTitle,
        subheader: hasPriceList ? activePriceEntry?.currency ?? "" : "",
        description: documentToHtmlString(item?.description?.json),
        id: cardId,
        button: buttonData?.[0],
        priceDescription: item?.priceDescription ? item.priceDescription : "",
        detailsHeader: item?.detailsSectionHeader ? item.detailsSectionHeader : "",
        currency: activeCurrency,
        details: item?.details ? item.details.map((detail: any) => {
          return {
            text: detail
          };
        }) : [],
        annual: item?.subscription?.[0] === "Annual" ? true : false,
        monthly: item?.subscription?.[0] === "Monthly" ? true : false,
        notApplicable: item?.subscription?.[0] === "N/A" ? true : item?.subscription?.[0] !== "Annual" && item?.subscription?.[0] !== "Monthly" && item?.subscription?.[0] !== "N/A" ? true : false,
        priceList: hasPriceList ? priceList : {},
        discount: manualOverride ? activePriceEntry?.discount ?? item?.discount ?? "" : activePriceEntry?.discount ?? "",
        featured: item?.featured,
        featuredText: item?.featureText,
        discountCode: manualOverride ? activePriceEntry?.discountCode ?? item?.discountCode ?? "" : activePriceEntry?.discountCode ?? ""
      };
    });
  };
  const dividerClasses = `section-divider-wrap ${value.theme}`;
  const formatTabs = value?.pricingCardsInTabsCollection?.items?.filter(Boolean).map((tab: any) => {
    return {
      title: tab?.title,
      cards: cardData(tab?.cardsCollection?.items ?? [])
    };
  });
  const formatCurrencyList = formatLayoutCurrencyList(value?.currencyList);
  const pricingCardData = useMemo(() => {
    return {
      eyebrow: value?.eyebrow || "",
      header: value?.header || "",
      subheader: value?.subheader || "",
      description: value?.description?.json ? documentToHtmlString(value.description.json) : "",
      buttons,
      headerElement,
      presetTheme: value?.theme,
      isWrapped: true,
      subscriptionType: value?.toggleSubscriptionTypes ? [{
        machineName: "monthly",
        name: value?.toggleSubscriptionOption2
      }, {
        machineName: "annual",
        name: value?.toggleSubscriptionOption1
      }] : "",
      toggleOptions: value?.toggleSubscriptionTypes ? [value?.toggleSubscriptionOption2, value?.toggleSubscriptionOption1] : undefined,
      excludesTaxLabel: value?.excludesTaxText || "",
      paymentText: value?.paymentFinePrint || "",
      comparePlansText: value?.comparePlansPricingTextLabel || "",
      displayCardPaymentOptionsMedia: value?.displayCardPaymentOptionsMedia || "",
      cardType: "normal",
      cards: formatTabs?.length ? [] : cardData(value.pricingCardsCollection?.items) || [],
      tabs: formatTabs,
      currencies: formatCurrencyList ?? undefined,
      additionalPricingInformation: value?.additionalPricingInformation ? documentToHtmlString(value?.additionalPricingInformation?.json) : "",
      viewMoreLabel,
      viewLessLabel
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value), productPriceLists, state?.currency, viewMoreLabel, viewLessLabel]);
  return <div className={`w-full ${value.theme}`}>
      <LayoutPricingCards {...pricingCardData} key={index} />
      {divider && <div className={`w-full px-5 lg:px-24 m-auto max-w-[1440px] ${dividerClasses}`}>
          <ThemeDivider currentTheme={value.theme} />
        </div>}
    </div>;
}